import { computed, h, type Ref, type VNode } from 'vue';
import { RouterLink } from 'vue-router';
import {
  NEllipsis,
  NIcon,
  NPopover,
  NSpace,
  NTag,
  useThemeVars,
  type DataTableBaseColumn,
  type DataTableColumns,
} from 'naive-ui';
import {
  PlaybookEvaluationType,
  PlaybookRunStatus,
  TaskStatus,
  type ClauseValidationOptionType,
  type ListPlaybookEvaluationsResponse,
  type PlaybookEvaluationGroupInDb,
  type PlaybookRiskLevel,
  type PlaybookRunInDb,
} from '@/api';
import {
  actionResultDetail,
  actionResultTypeLabel,
  actionTypeIcon,
  averageFindingBucket,
  buildRedlineSegments,
  clauseOptionTypeShortLabel,
  extractedValue,
  extractionResponseType,
  firedGroupActions,
  groupDisplayName,
  groupsByTriggerUid,
  isExtractionResult,
  isUncommonsResult,
  parseSuggestionRedlines,
  riskTagType,
  severityTagType,
  stepStatusTagType,
  uncommonsFindings,
  type ReplacementSuggestion,
} from '@/services/playbooks/playbookRunDisplay';
import GroupActionIcons from '@/components/playbooks/run-results/GroupActionIcons.vue';
import { gaiaTooltipProps } from '@/components/design/base/GaiaTooltip.vue';
import { gaiaStatus } from '@/components/design/themes/tokens';
import { formatDisplayValue } from '@/utils/displayAiResponse';
import { getListItemType } from '@/utils/fieldType';
import { signingRequestEditRoute } from '@/router/links';

type TagType = 'default' | 'success' | 'info' | 'warning' | 'error';
export type PlaybookEvaluation = ListPlaybookEvaluationsResponse[number];
export type EvaluationResult = NonNullable<PlaybookRunInDb['evaluation_results']>[number];
type ActionResult = NonNullable<EvaluationResult['action_results']>[number];
type EvaluationType = PlaybookEvaluation['evaluation_type'];

export const runStatusType: Record<PlaybookRunStatus, TagType> = {
  [PlaybookRunStatus.PENDING]: 'default',
  [PlaybookRunStatus.DONE]: 'success',
  [PlaybookRunStatus.RUNNING]: 'info',
  [PlaybookRunStatus.SUMMARIZING]: 'info',
  [PlaybookRunStatus.FAILED]: 'error',
  [PlaybookRunStatus.PARTIAL]: 'warning',
  [PlaybookRunStatus.SKIPPED]: 'default',
  [PlaybookRunStatus.CANCELLED]: 'default',
  [PlaybookRunStatus.WARNING]: 'warning',
};

const EVALUATION_TYPE_LABEL: Record<EvaluationType, string> = {
  [PlaybookEvaluationType.EXTRACTION]: 'Extraction',
  [PlaybookEvaluationType.UNCOMMONS_CHECK]: 'Uncommons check',
  [PlaybookEvaluationType.CLAUSE_VALIDATION]: 'Clause validation',
  [PlaybookEvaluationType.CLASSIFICATION]: 'Classification',
};

// Left-to-right order of the type groups.
const EVALUATION_TYPE_ORDER: EvaluationType[] = [
  PlaybookEvaluationType.EXTRACTION,
  PlaybookEvaluationType.UNCOMMONS_CHECK,
  PlaybookEvaluationType.CLAUSE_VALIDATION,
  PlaybookEvaluationType.CLASSIFICATION,
];

/** One selectable value of a column filter, with the number of columns it covers. */
export interface EvaluationFilterBucket {
  key: string;
  label: string;
  count: number;
}

/** Types actually present in the evaluation list, in column order, with their column counts. */
export function evaluationTypeBuckets(
  evaluations: ListPlaybookEvaluationsResponse
): EvaluationFilterBucket[] {
  return EVALUATION_TYPE_ORDER.map(type => ({
    key: type,
    label: EVALUATION_TYPE_LABEL[type],
    count: evaluations.filter(evaluation => evaluation.evaluation_type === type).length,
  })).filter(bucket => bucket.count > 0);
}

/** Non-empty evaluation groups with their column counts. */
export function evaluationGroupBuckets(
  evaluations: ListPlaybookEvaluationsResponse,
  groups: PlaybookEvaluationGroupInDb[]
): EvaluationFilterBucket[] {
  const knownUids = new Set(evaluations.map(evaluation => evaluation.uid));
  return groups
    .map(group => ({
      key: group.uid,
      label: groupDisplayName(group),
      count: (group.evaluation_uids ?? []).filter(uid => knownUids.has(uid)).length,
    }))
    .filter(bucket => bucket.count > 0);
}

/**
 * Whether each filter row is worth showing. Every evaluation has exactly one
 * type, so a lone type bucket covers all columns and filters nothing — groups
 * cover a subset by definition, so a single one is already a useful filter.
 */
export function showTypeFilterRow(buckets: EvaluationFilterBucket[]): boolean {
  return buckets.length > 1;
}

export function showGroupFilterRow(buckets: EvaluationFilterBucket[]): boolean {
  return buckets.length > 0;
}

/** Evaluation uids covered by the selected group buckets. */
function evaluationUidsForGroups(
  groups: PlaybookEvaluationGroupInDb[],
  selectedKeys: string[]
): Set<string> {
  const selected = new Set(selectedKeys);
  const uids = new Set<string>();
  for (const group of groups) {
    if (!selected.has(group.uid)) continue;
    for (const uid of group.evaluation_uids ?? []) uids.add(uid);
  }
  return uids;
}

/**
 * The evaluations in the order their columns appear: bucketed by type, in the
 * fixed type order the grid lays them out in. A column picker lists them this
 * way so the checklist reads left-to-right like the table it controls.
 */
export function evaluationColumnOrder(
  evaluations: ListPlaybookEvaluationsResponse
): ListPlaybookEvaluationsResponse {
  return EVALUATION_TYPE_ORDER.flatMap(type =>
    evaluations.filter(evaluation => evaluation.evaluation_type === type)
  );
}

/** Column filter selections; an empty array on a dimension means "no filter". */
export interface EvaluationColumnFilter {
  types: string[];
  groups: string[];
  /**
   * Evaluations the user has unticked in the column picker, by uid. A deny list
   * rather than an allow list on purpose: a playbook gains evaluations over
   * time, and an allow list would silently hide every new one from anybody who
   * had ever touched the picker.
   */
  hidden?: string[];
}

/** The evaluations whose columns survive every filter (the dimensions are ANDed). */
export function filterEvaluationColumns(
  evaluations: ListPlaybookEvaluationsResponse,
  groups: PlaybookEvaluationGroupInDb[],
  filter: EvaluationColumnFilter
): ListPlaybookEvaluationsResponse {
  const groupUids = filter.groups.length ? evaluationUidsForGroups(groups, filter.groups) : null;
  const hidden = new Set(filter.hidden ?? []);
  return evaluations.filter(
    evaluation =>
      (filter.types.length === 0 || filter.types.includes(evaluation.evaluation_type)) &&
      (groupUids === null || groupUids.has(evaluation.uid)) &&
      !hidden.has(evaluation.uid)
  );
}

// An outcome tag shrinks with its cell instead of holding its content width,
// so a crowded cell truncates the label rather than pushing it under the icons.
const TAG_STYLE = 'max-width: 100%;';

const EXTRACTION_CELL_STYLE =
  'display: inline-block; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; vertical-align: bottom; cursor: default;';

/** Pick the latest result for a evaluation within a run (by is_latest, then attempt). */
function latestResultFor(run: PlaybookRunInDb, evaluationUid: string): EvaluationResult | null {
  const all = (run.evaluation_results ?? []).filter(r => r.evaluation_uid === evaluationUid);
  if (all.length === 0) return null;
  const latest = all.filter(r => r.is_latest);
  const pool = latest.length > 0 ? latest : all;
  return pool.reduce((best, current) =>
    current.attempt_number > best.attempt_number ? current : best
  );
}

/**
 * Wrap a tag label so it truncates within the column and reveals the full text
 * on hover. The cap is the room the cell leaves rather than a fixed width: the
 * action icons share that row, so how much there is depends on how many fired.
 */
function ellipsisLabel(label: string): VNode {
  return h(
    NEllipsis,
    { tooltip: gaiaTooltipProps, style: 'max-width: 100%;' },
    { default: () => label }
  );
}

/** True when a formatted value carries no real content. */
function isEmptyFormatted(formatted: string): boolean {
  return formatted === 'N/A' || formatted === 'Empty';
}

/** Reasoning block for popover content — label + text, or null if no reasoning. */
function renderReasoningBlock(reasoning: string | null | undefined): VNode | null {
  if (!reasoning) return null;
  return h('div', { style: 'margin-top: 8px;' }, [
    h('div', { style: 'font-weight: 600; margin-bottom: 4px; font-size: 12px;' }, 'Reasoning'),
    h(
      'div',
      {
        style: 'white-space: pre-wrap; word-break: break-word; font-size: 12px; line-height: 1.5;',
      },
      reasoning
    ),
  ]);
}

/** Full extracted value for the hover popover — one line per item for lists. */
function renderExtractionFull(result: EvaluationResult, evaluation: PlaybookEvaluation): VNode {
  const responseType = extractionResponseType(evaluation) ?? undefined;
  const value = extractedValue(result);
  const header = h('div', { style: 'font-weight: 600; margin-bottom: 6px;' }, 'Extracted value');

  if (Array.isArray(value)) {
    const itemType = responseType ? getListItemType(responseType) : undefined;
    const rows = value.map(item =>
      h('div', { style: 'margin-bottom: 2px;' }, formatDisplayValue(item, itemType))
    );
    return h('div', { style: 'max-width: 320px; max-height: 320px; overflow: auto;' }, [
      header,
      ...rows,
      renderReasoningBlock(result.reasoning),
    ]);
  }

  return h(
    'div',
    { style: 'max-width: 320px; max-height: 320px; overflow: auto; white-space: pre-wrap;' },
    [header, formatDisplayValue(value, responseType), renderReasoningBlock(result.reasoning)]
  );
}

export interface PlaybookRunCells {
  renderEvaluationCell: (run: PlaybookRunInDb, evaluation: PlaybookEvaluation) => VNode;
  buildEvaluationColumnGroups: () => DataTableColumns<PlaybookRunInDb>;
  renderGroupActionsCell: (run: PlaybookRunInDb) => VNode;
}

export interface PlaybookRunCellsOptions {
  /** Makes cells with a result clickable (e.g. to show the result's citations). */
  onResultClick?: (
    run: PlaybookRunInDb,
    evaluation: PlaybookEvaluation,
    result: EvaluationResult
  ) => void;
  /** Evaluation groups, needed to tell group-triggered actions from option-triggered ones. */
  evaluationGroups?: Ref<PlaybookEvaluationGroupInDb[]>;
  /**
   * Draw the per-result action indicators. Off for a review project's analysis:
   * it starts every run with `skip_action_types` covering every type, so what
   * comes back is evaluations only and the actions would render as a row of
   * grey skipped icons under each cell — noise about work that never ran.
   */
  showActions?: boolean;
}

/**
 * Theme-aware render functions for playbook-run evaluation cells and their
 * grouped n-data-table columns, shared by every table that shows runs
 * (PlaybookRunsView, the review-project analysis data table).
 *
 * Tables using the grouped columns must also carry the `.eval-group-start`
 * divider CSS and set the `--eval-group-divider` CSS variable.
 */
export function usePlaybookRunCells(
  evaluations: Ref<ListPlaybookEvaluationsResponse>,
  options: PlaybookRunCellsOptions = {}
): PlaybookRunCells {
  const themeVars = useThemeVars();

  /** Muted placeholder style (em-dash) for empty cells, theme-aware. */
  const placeholderStyle = computed<string>(() => `color: ${themeVars.value.textColor3};`);

  /**
   * Icon tint per step status — green = done, red = failed, violet = running,
   * grey otherwise. Running comes from the Gaia status scale rather than
   * `primaryColor`, which is ink: a running action reads as running here and on
   * a GaiaStatusIndicator, not as the theme's primary.
   */
  const stepStatusColorMap = computed<Record<TaskStatus, string>>(() => ({
    [TaskStatus.DONE]: themeVars.value.successColor,
    [TaskStatus.RUNNING]: gaiaStatus.running,
    [TaskStatus.FAILED]: themeVars.value.errorColor,
    [TaskStatus.SKIPPED]: themeVars.value.textColor3,
    [TaskStatus.PENDING]: themeVars.value.textColor3,
    [TaskStatus.PARTIAL]: themeVars.value.warningColor,
    [TaskStatus.CANCELLED]: themeVars.value.textColor3,
    [TaskStatus.WARNING]: themeVars.value.warningColor,
  }));

  function stepStatusColor(status: TaskStatus): string {
    return stepStatusColorMap.value[status];
  }

  /** Resolve every option uid → its display metadata across all evaluations. */
  const optionMap = computed<
    Map<
      string,
      {
        name: string;
        riskLevel?: PlaybookRiskLevel | null;
        optionType?: ClauseValidationOptionType | null;
      }
    >
  >(() => {
    const map = new Map<
      string,
      {
        name: string;
        riskLevel?: PlaybookRiskLevel | null;
        optionType?: ClauseValidationOptionType | null;
      }
    >();
    for (const evaluation of evaluations.value) {
      for (const option of evaluation.options ?? []) {
        map.set(option.uid, {
          name: option.name,
          riskLevel: option.risk_level,
          optionType: 'option_type' in option ? option.option_type : null,
        });
      }
    }
    return map;
  });

  function optionTagVNode(optionUid: string): VNode {
    const option = optionMap.value.get(optionUid);
    const label = option?.optionType
      ? clauseOptionTypeShortLabel(option.optionType)
      : (option?.name ?? 'Unknown option');
    return h(
      NTag,
      { size: 'small', type: riskTagType(option?.riskLevel), style: TAG_STYLE },
      { default: () => ellipsisLabel(label) }
    );
  }

  function renderOutcome(result: EvaluationResult): VNode {
    if (isUncommonsResult(result)) {
      const findings = uncommonsFindings(result);
      if (findings.length === 0) {
        return h('span', { style: placeholderStyle.value }, '—');
      }
      const label = `${findings.length} finding${findings.length > 1 ? 's' : ''}`;
      return h(
        NTag,
        { size: 'small', type: severityTagType(averageFindingBucket(findings)), style: TAG_STYLE },
        { default: () => ellipsisLabel(label) }
      );
    }
    const optionUids = result.matched_option_uids ?? [];
    if (optionUids.length > 0) {
      return h(NSpace, { size: 4, wrapItem: false }, () =>
        optionUids.map(uid => optionTagVNode(uid))
      );
    }
    return h(
      NTag,
      { size: 'small', type: stepStatusTagType(result.status), style: TAG_STYLE },
      { default: () => ellipsisLabel(result.status) }
    );
  }

  /** Inline before/after preview of a single redline for the action popover. */
  function renderRedline(redline: ReplacementSuggestion): VNode {
    const segments = buildRedlineSegments(redline.string_to_replace, redline.replacement_string);
    const segmentStyle: Record<string, string> = {
      unchanged: '',
      removed: 'color: #a33b36; text-decoration: line-through;',
      added: 'color: #166534; background: #dcfce7; border-radius: 3px; padding: 0 2px;',
    };
    return h('div', { style: 'margin-top: 6px;' }, [
      h(
        'div',
        {
          style:
            'font-size: 12px; line-height: 1.5; white-space: pre-wrap; word-break: break-word;',
        },
        segments.map(segment => h('span', { style: segmentStyle[segment.kind] }, segment.text))
      ),
      redline.reason
        ? h(
            'div',
            {
              style: `color: ${themeVars.value.textColor3}; font-size: 11px; margin-top: 2px;`,
            },
            [h('strong', 'Reason:'), ` ${redline.reason}`]
          )
        : null,
    ]);
  }

  /**
   * Link straight to the signature request a signature-preparation action created.
   * Stops propagation so following it doesn't also open the run behind the popover.
   */
  function renderSigningRequestLink(action: ActionResult): VNode | null {
    if (action.action_type !== 'signature_preparation' || !action.signing_request_uid) return null;
    return h(
      RouterLink,
      {
        to: signingRequestEditRoute(action.signing_request_uid),
        onClick: (e: MouseEvent): void => {
          e.stopPropagation();
        },
        style: 'display: inline-block; margin-top: 6px; font-size: 12px;',
      },
      { default: () => 'Open signature request' }
    );
  }

  /** Hover popover content for a single action — label, status tag and detail. */
  function renderActionPopover(action: ActionResult): VNode {
    const redlines =
      action.action_type === 'suggestion' ? parseSuggestionRedlines(action.suggestion) : [];
    const detail = redlines.length ? '' : actionResultDetail(action);
    return h('div', { style: 'max-width: 360px; max-height: 360px; overflow: auto;' }, [
      h('div', { style: 'display: flex; align-items: center; gap: 8px;' }, [
        h('span', { style: 'font-weight: 600;' }, actionResultTypeLabel(action.action_type)),
      ]),
      ...redlines.map(renderRedline),
      detail
        ? h(
            'div',
            { style: `color: ${themeVars.value.textColor3}; font-size: 12px; margin-top: 4px;` },
            detail
          )
        : null,
      renderSigningRequestLink(action),
    ]);
  }

  const groupByTriggerUid = computed<Map<string, PlaybookEvaluationGroupInDb>>(() =>
    groupsByTriggerUid(options.evaluationGroups?.value ?? [])
  );

  /**
   * Top-right cluster of one small icon per action that ran on this result; each icon hovers
   * to reveal that action's stats. Returns null when no actions ran (so the cell stays clean),
   * and when the caller has no actions to speak of at all (`showActions: false`).
   *
   * Group-triggered actions are left out: they belong to the group, not to whichever
   * member evaluation happened to record them, and have their own column.
   */
  function renderActionIcons(result: EvaluationResult): VNode | null {
    if (options.showActions === false) return null;
    const actions = (result.action_results ?? []).filter(
      a => a.is_latest && !groupByTriggerUid.value.has(a.action_trigger_uid)
    );
    if (actions.length === 0) return null;

    const icons = actions.map(action =>
      h(
        NPopover,
        { trigger: 'hover', placement: 'top' },
        {
          trigger: () =>
            h(
              NIcon,
              { size: 16, color: stepStatusColor(action.status), style: 'cursor: default;' },
              { default: () => h(actionTypeIcon(action.action_type)) }
            ),
          default: () => renderActionPopover(action),
        }
      )
    );

    return h('div', { style: 'display: flex; gap: 4px;' }, icons);
  }

  /**
   * The group-triggered actions that ran on this run. Counterpart to the exclusion
   * in `renderActionIcons`; the icons and their hover come from GroupActionIcons so
   * the runs table and the run detail read identically.
   */
  function renderGroupActionsCell(run: PlaybookRunInDb): VNode {
    const fired = firedGroupActions(run, options.evaluationGroups?.value ?? []);
    if (fired.length === 0) return h('span', { style: placeholderStyle.value }, '—');
    return h(GroupActionIcons, { fired });
  }

  /** Popover body for non-extraction results (classification, clause validation, uncommons check). */
  function renderOutcomeFull(result: EvaluationResult): VNode {
    const outcome = renderOutcome(result);
    return h('div', { style: 'max-width: 320px; max-height: 320px; overflow: auto;' }, [
      h('div', { style: 'font-weight: 600; margin-bottom: 6px;' }, 'Outcome'),
      h('div', { style: 'margin-bottom: 4px;' }, outcome),
      renderReasoningBlock(result.reasoning),
    ]);
  }

  /** The cell body — the extracted value (with its value-preview popover) or the outcome tag(s). */
  function renderEvaluationBody(result: EvaluationResult, evaluation: PlaybookEvaluation): VNode {
    if (isExtractionResult(result)) {
      const responseType = extractionResponseType(evaluation) ?? undefined;
      const formatted = formatDisplayValue(extractedValue(result), responseType);
      if (isEmptyFormatted(formatted)) {
        if (!result.reasoning) {
          return h('span', { style: placeholderStyle.value }, '—');
        }
        return h(
          NPopover,
          { trigger: 'hover', placement: 'top' },
          {
            trigger: () => h('span', { style: placeholderStyle.value }, '—'),
            default: () => renderExtractionFull(result, evaluation),
          }
        );
      }
      return h(
        NPopover,
        { trigger: 'hover', placement: 'top' },
        {
          trigger: () => h('span', { style: EXTRACTION_CELL_STYLE }, formatted),
          default: () => renderExtractionFull(result, evaluation),
        }
      );
    }
    if (!result.reasoning) {
      return renderOutcome(result);
    }
    return h(
      NPopover,
      { trigger: 'hover', placement: 'top' },
      {
        trigger: () => renderOutcome(result),
        default: () => renderOutcomeFull(result),
      }
    );
  }

  function renderEvaluationCell(run: PlaybookRunInDb, evaluation: PlaybookEvaluation): VNode {
    const result = latestResultFor(run, evaluation.uid);
    if (!result) {
      return h('span', { style: placeholderStyle.value }, '—');
    }

    const onClick = options.onResultClick;
    const clickProps = onClick
      ? {
          onClick: (): void => {
            onClick(run, evaluation, result);
          },
        }
      : {};
    const cursor = onClick ? ' cursor: pointer;' : '';

    // Clip the body to the column width so long tags / multi-tag outcomes never
    // bleed sideways.
    const body = h('div', { style: 'min-width: 0; overflow: hidden;' }, [
      renderEvaluationBody(result, evaluation),
    ]);
    const icons = renderActionIcons(result);
    if (!icons) return h('div', { ...clickProps, style: cursor }, [body]);

    // The icons sit on their own line under the result. Beside it they competed
    // with the outcome tag for a 200px column — five fired actions left the tag
    // nothing — and over it, as an absolute top-right cluster, they grew
    // leftwards across it. Stacked, each gets the full width of the cell.
    return h(
      'div',
      { ...clickProps, style: `display: flex; flex-direction: column; gap: 7px;${cursor}` },
      [body, icons]
    );
  }

  function buildEvaluationColumnGroups(): DataTableColumns<PlaybookRunInDb> {
    // One leaf column per evaluation; its current shape is unchanged.
    const leafColumn = (evaluation: PlaybookEvaluation): DataTableBaseColumn<PlaybookRunInDb> => ({
      title: evaluation.name,
      key: `cond:${evaluation.uid}`,
      width: 200,
      render: (row): VNode => renderEvaluationCell(row, evaluation),
    });

    // Bucket evaluations by type, then emit one group per type (in a fixed order)
    // so columns of the same type sit contiguously under a single header.
    const byType = new Map<EvaluationType, PlaybookEvaluation[]>();
    const ungrouped: PlaybookEvaluation[] = [];
    for (const evaluation of evaluations.value) {
      const type = evaluation.evaluation_type;
      if (type in EVALUATION_TYPE_LABEL) {
        const bucket = byType.get(type) ?? [];
        bucket.push(evaluation);
        byType.set(type, bucket);
      } else {
        ungrouped.push(evaluation);
      }
    }

    const evaluationGroups: DataTableColumns<PlaybookRunInDb> = [];
    for (const type of EVALUATION_TYPE_ORDER) {
      const bucket = byType.get(type);
      if (!bucket?.length) continue;
      // Draw a vertical divider at the start of every group so the type
      // boundaries are visible (also separates the static columns from the first).
      const children = bucket.map(leafColumn);
      if (children[0]) {
        children[0].className = 'eval-group-start';
      }
      evaluationGroups.push({
        title: EVALUATION_TYPE_LABEL[type],
        key: `evalgroup:${type}`,
        titleAlign: 'center',
        className: 'eval-group-start',
        children,
      });
    }
    // Defensive: any unrecognized type stays ungrouped, preserving prior behavior.
    evaluationGroups.push(...ungrouped.map(leafColumn));

    return evaluationGroups;
  }

  return { renderEvaluationCell, buildEvaluationColumnGroups, renderGroupActionsCell };
}
